import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAppContext } from "@/components/DolphinLayout";
import {
  TelegramLogo,
  CheckCircle,
  XCircle,
  ArrowsClockwise,
  Info,
  User,
  Phone,
  Key,
  SignOut,
  Download,
  Cookie,
  ListBullets,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TelegramConnect = () => {
  const { refreshStats, refreshFolders } = useAppContext();
  
  // User auth state
  const [authStatus, setAuthStatus] = useState({ authenticated: false, user: null });
  const [authLoading, setAuthLoading] = useState(true);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState("phone"); // phone, code, 2fa
  const [sending, setSending] = useState(false);
  
  // Group parsing state
  const [groupUsername, setGroupUsername] = useState("");
  const [parseLimit, setParseLimit] = useState(100);

  // Load bot username from settings
  useEffect(() => {
    axios.get(`${API}/settings/bot-username`).then(res => {
      setGroupUsername(res.data.username || "ozon_48230_block_bot");
    }).catch(() => setGroupUsername("ozon_48230_block_bot"));
  }, []);
  const [parsing, setParsing] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [parsingCards, setParsingCards] = useState(false);
  const [cardListResult, setCardListResult] = useState(null);
  
  const handleParseCardList = async () => {
    setParsingCards(true);
    setCardListResult(null);
    try {
      const res = await axios.post(`${API}/telegram/parse-card-list`, {
        bot_username: groupUsername.replace("@", ""),
      });
      setCardListResult(res.data);
      toast.success(`Карты: ${res.data.total_cards} (${res.data.total_pages} стр), создано: ${res.data.created}, обновлено: ${res.data.updated}`);
      refreshStats?.();
      refreshFolders?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ошибка парсинга карт");
      setCardListResult({ success: false, error: e.response?.data?.detail });
    } finally {
      setParsingCards(false);
    }
  };

  // Cookies state
  const [cookiePhone, setCookiePhone] = useState("");
  const [gettingCookies, setGettingCookies] = useState(false);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    setAuthLoading(true);
    try {
      const res = await axios.get(`${API}/telegram/user-auth-status`);
      setAuthStatus(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSendCode = async () => {
    if (!phone.trim()) {
      toast.error("Введите номер телефона");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/telegram/send-code`, { phone });
      if (res.data.success) {
        toast.success("Код отправлен в Telegram");
        setStep("code");
      } else {
        toast.error(res.data.error || "Ошибка отправки кода");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ошибка");
    } finally {
      setSending(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!code.trim()) {
      toast.error("Введите код");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/telegram/verify-code`, { 
        phone, 
        code,
        password: step === "2fa" ? password : null
      });
      
      if (res.data.success) {
        toast.success("Авторизация успешна!");
        setAuthStatus({ authenticated: true, user: res.data.user });
        setStep("phone");
        setPhone("");
        setCode("");
        setPassword("");
      } else if (res.data.needs_2fa) {
        toast.info("Требуется пароль двухфакторной аутентификации");
        setStep("2fa");
      } else {
        toast.error(res.data.error || "Неверный код");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ошибка");
    } finally {
      setSending(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/telegram/logout`);
      toast.success("Вы вышли из Telegram");
      setAuthStatus({ authenticated: false, user: null });
    } catch (e) {
      toast.error("Ошибка выхода");
    }
  };

  const handleParseGroup = async () => {
    if (!groupUsername.trim()) {
      toast.error("Введите username группы/бота");
      return;
    }
    setParsing(true);
    setParseResult(null);
    try {
      const res = await axios.post(`${API}/telegram/parse-group`, {
        group_username: groupUsername.replace("@", ""),
        limit: parseLimit
      });
      setParseResult(res.data);
      toast.success(res.data.message);
      refreshStats?.();
      refreshFolders?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ошибка парсинга");
      setParseResult({ success: false, error: e.response?.data?.detail });
    } finally {
      setParsing(false);
    }
  };

  const handleGetCookies = async () => {
    if (!cookiePhone.trim()) {
      toast.error("Введите номер телефона");
      return;
    }
    setGettingCookies(true);
    try {
      const res = await axios.post(`${API}/telegram/get-cookies`, {
        phone: cookiePhone.replace(/[^0-9]/g, ""),
        bot_username: groupUsername || "ozon_48230_block_bot"
      });
      if (res.data.success) {
        toast.success(res.data.message);
        refreshStats?.();
      } else {
        toast.warning(res.data.message);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ошибка получения куков");
    } finally {
      setGettingCookies(false);
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <ArrowsClockwise size={24} className="animate-spin text-[#2196F3]" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="telegram-connect-page">
      {/* Top bar */}
      <div className="top-bar">
        <div className="top-bar-left">
          <TelegramLogo size={24} weight="duotone" className="text-[#00BCD4]" />
          <h1 className="text-lg font-semibold">Telegram Интеграция</h1>
        </div>
      </div>

      {/* Content */}
      <div className="content-area">
        <div className="max-w-2xl mx-auto space-y-6">
          
          {/* Auth status */}
          {authStatus.authenticated ? (
            <div className="bg-[#4CAF50]/10 border border-[#4CAF50]/30 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#4CAF50]/20 rounded-full flex items-center justify-center">
                    <User size={24} className="text-[#4CAF50]" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <CheckCircle size={18} className="text-[#4CAF50]" />
                      <span className="font-semibold text-[#4CAF50]">Авторизован</span>
                    </div>
                    <p className="text-sm text-[#8A9099]">
                      {authStatus.user?.first_name} {authStatus.user?.last_name}
                      {authStatus.user?.username && ` (@${authStatus.user.username})`}
                    </p>
                    <p className="text-xs text-[#5C6370] font-mono">{authStatus.user?.phone}</p>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="action-btn action-btn-outline text-red-400 border-red-400"
                  data-testid="logout-btn"
                >
                  <SignOut size={14} />
                  Выйти
                </button>
              </div>
            </div>
          ) : (
            /* Auth form */
            <div className="bg-[#1A1D24] rounded-lg border border-[#252A33] p-6 space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00BCD4]/20 rounded-lg flex items-center justify-center">
                  <TelegramLogo size={24} className="text-[#00BCD4]" />
                </div>
                <div className="flex-1">
                  <h2 className="text-base font-semibold mb-1">Авторизация в Telegram</h2>
                  <p className="text-sm text-[#8A9099]">
                    Войдите в свой аккаунт для парсинга профилей из бота
                  </p>
                </div>
              </div>

              {step === "phone" && (
                <div className="space-y-4 pt-4">
                  <div>
                    <Label className="text-xs text-[#8A9099]">Номер телефона</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+7 999 123 4567"
                        className="bg-[#0D0F12] border-[#252A33] font-mono"
                        data-testid="phone-input"
                      />
                      <button
                        onClick={handleSendCode}
                        disabled={sending}
                        className="action-btn action-btn-primary whitespace-nowrap"
                        data-testid="send-code-btn"
                      >
                        {sending ? <ArrowsClockwise size={14} className="animate-spin" /> : <Phone size={14} />}
                        Отправить код
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {step === "code" && (
                <div className="space-y-4 pt-4">
                  <div className="bg-[#2196F3]/10 border border-[#2196F3]/30 rounded p-3 text-sm">
                    <p className="text-[#2196F3]">Код отправлен на {phone}</p>
                    <p className="text-xs text-[#8A9099] mt-1">Проверьте Telegram</p>
                  </div>
                  <div>
                    <Label className="text-xs text-[#8A9099]">Код подтверждения</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder="12345"
                        className="bg-[#0D0F12] border-[#252A33] font-mono text-center text-lg tracking-widest"
                        maxLength={5}
                        data-testid="code-input"
                      />
                      <button
                        onClick={handleVerifyCode}
                        disabled={sending}
                        className="action-btn action-btn-primary"
                        data-testid="verify-code-btn"
                      >
                        {sending ? <ArrowsClockwise size={14} className="animate-spin" /> : <Key size={14} />}
                        Подтвердить
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() => setStep("phone")}
                    className="text-sm text-[#8A9099] hover:text-white"
                  >
                    ← Изменить номер
                  </button>
                </div>
              )}

              {step === "2fa" && (
                <div className="space-y-4 pt-4">
                  <div className="bg-[#FF9800]/10 border border-[#FF9800]/30 rounded p-3 text-sm">
                    <p className="text-[#FF9800]">Требуется двухфакторная аутентификация</p>
                  </div>
                  <div>
                    <Label className="text-xs text-[#8A9099]">Пароль 2FA</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Пароль"
                        className="bg-[#0D0F12] border-[#252A33]"
                        data-testid="2fa-password-input"
                      />
                      <button
                        onClick={handleVerifyCode}
                        disabled={sending}
                        className="action-btn action-btn-primary"
                        data-testid="verify-2fa-btn"
                      >
                        {sending ? <ArrowsClockwise size={14} className="animate-spin" /> : <Key size={14} />}
                        Войти
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Parse profiles section - only if authenticated */}
          {authStatus.authenticated && (
            <>
              <div className="bg-[#1A1D24] rounded-lg border border-[#252A33] p-6 space-y-4">
                <h2 className="text-sm font-semibold text-[#8A9099]">ПАРСИНГ ПРОФИЛЕЙ ИЗ БОТА</h2>
                <p className="text-sm text-[#5C6370]">
                  Парсинг номеров телефонов из сообщений бота. Названия профилей = номера телефонов.
                  Дубликаты автоматически пропускаются.
                </p>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <Label className="text-xs text-[#8A9099]">Username бота</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm text-[#5C6370]">@</span>
                      <Input
                        value={groupUsername}
                        onChange={(e) => setGroupUsername(e.target.value.replace('@', ''))}
                        placeholder="ozon_48230_block_bot"
                        className="bg-[#0D0F12] border-[#252A33] font-mono"
                        data-testid="group-username-input"
                      />
                      <button
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/settings/bot-username`, { username: groupUsername });
                            toast.success("Bot saved");
                          } catch { toast.error("Error"); }
                        }}
                        className="h-9 px-3 rounded bg-[#2196F3] hover:bg-[#1976D2] text-white text-xs font-medium whitespace-nowrap"
                        data-testid="save-bot-username-btn"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-[#8A9099]">Лимит сообщений</Label>
                    <Input
                      type="number"
                      value={parseLimit}
                      onChange={(e) => setParseLimit(parseInt(e.target.value) || 100)}
                      className="mt-1 bg-[#0D0F12] border-[#252A33]"
                      data-testid="parse-limit-input"
                    />
                  </div>
                </div>

                <button
                  onClick={handleParseGroup}
                  disabled={parsing}
                  className="action-btn action-btn-primary"
                  style={{ background: "#00BCD4" }}
                  data-testid="parse-group-btn"
                >
                  {parsing ? (
                    <>
                      <ArrowsClockwise size={14} className="animate-spin" />
                      Парсинг...
                    </>
                  ) : (
                    <>
                      <Download size={14} />
                      Загрузить профили
                    </>
                  )}
                </button>

                <button
                  onClick={handleParseCardList}
                  disabled={parsingCards}
                  className="action-btn action-btn-primary"
                  style={{ background: "#FF9800" }}
                  data-testid="parse-card-list-btn"
                >
                  {parsingCards ? (
                    <>
                      <ArrowsClockwise size={14} className="animate-spin" />
                      Парсинг карт...
                    </>
                  ) : (
                    <>
                      <ListBullets size={14} />
                      Список карт (все стр.)
                    </>
                  )}
                </button>

                {cardListResult && cardListResult.success && (
                  <div className="p-4 rounded bg-[#FF9800]/10 border border-[#FF9800]/30">
                    <div className="text-sm space-y-1">
                      <p className="text-[#FF9800] font-medium">Карт: {cardListResult.total_cards} ({cardListResult.total_pages} стр)</p>
                      <div className="text-xs text-[#8A9099]">
                        <p>Создано: {cardListResult.created} | Обновлено: {cardListResult.updated}</p>
                      </div>
                    </div>
                  </div>
                )}

                {parseResult && (
                  <div
                    className={`p-4 rounded ${
                      parseResult.success
                        ? "bg-[#4CAF50]/10 border border-[#4CAF50]/30"
                        : "bg-[#F44336]/10 border border-[#F44336]/30"
                    }`}
                  >
                    {parseResult.success ? (
                      <div className="text-sm space-y-1">
                        <p className="text-[#4CAF50] font-medium">{parseResult.message}</p>
                        <div className="text-xs text-[#8A9099]">
                          <p>Сообщений обработано: {parseResult.messages_parsed}</p>
                          <p>Профилей найдено: {parseResult.profiles_found}</p>
                          <p>Создано: {parseResult.profiles_created}</p>
                          <p>Пропущено (дубликаты): {parseResult.profiles_skipped}</p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-[#F44336]">{parseResult.error}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Get cookies section */}
              <div className="bg-[#1A1D24] rounded-lg border border-[#252A33] p-6 space-y-4">
                <h2 className="text-sm font-semibold text-[#8A9099]">ПОЛУЧИТЬ КУКИ ДЛЯ ПРОФИЛЯ</h2>
                <p className="text-sm text-[#5C6370]">
                  Отправляет команду <code className="bg-[#0D0F12] px-1 rounded">/dolphin {"{номер}"}</code> боту 
                  и сохраняет полученные куки в профиль
                </p>
                
                <div className="flex gap-2">
                  <Input
                    value={cookiePhone}
                    onChange={(e) => setCookiePhone(e.target.value)}
                    placeholder="79235074261"
                    className="bg-[#0D0F12] border-[#252A33] font-mono"
                    data-testid="cookie-phone-input"
                  />
                  <button
                    onClick={handleGetCookies}
                    disabled={gettingCookies}
                    className="action-btn action-btn-success whitespace-nowrap"
                    data-testid="get-cookies-btn"
                  >
                    {gettingCookies ? (
                      <>
                        <ArrowsClockwise size={14} className="animate-spin" />
                        Получение...
                      </>
                    ) : (
                      <>
                        <Cookie size={14} />
                        Получить куки
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Info */}
          <div className="bg-gradient-to-r from-[#2196F3]/10 to-transparent rounded-lg border border-[#2196F3]/30 p-6">
            <div className="flex items-start gap-3">
              <Info size={20} className="text-[#2196F3] mt-0.5" />
              <div>
                <h3 className="font-semibold mb-2">Как это работает</h3>
                <ol className="text-sm text-[#8A9099] space-y-2 list-decimal list-inside">
                  <li>Авторизуйтесь в Telegram через номер телефона</li>
                  <li>Нажмите "Загрузить профили" - номера телефонов будут спарсены из бота</li>
                  <li>Для получения куков введите номер и нажмите "Получить куки"</li>
                  <li>Бот получит команду /dolphin и куки будут сохранены в профиль</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TelegramConnect;
